import React, { useEffect, useMemo, useRef, useState } from 'react';
import ImportWizardHelp from './ImportWizardHelp.jsx';

function toSnakeCase(str) {
  return String(str || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s-]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function safeJsonParse(text) {
  if (!text) return null;
  const trimmed = String(text).trim();
  const unfenced = trimmed.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');

  try {
    return JSON.parse(unfenced);
  } catch {
    const first = unfenced.indexOf('{');
    const last = unfenced.lastIndexOf('}');
    if (first >= 0 && last > first) {
      try {
        return JSON.parse(unfenced.slice(first, last + 1));
      } catch {
        return null;
      }
    }
  }
  return null;
}

export default function WebpageImportWizard({ pageTitle = 'Import Website to Template' }) {
  const iframeRef = useRef(null);

  const [showHelp, setShowHelp] = useState(false);
  const [step, setStep] = useState(1);
  const [analysisStatus, setAnalysisStatus] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState('');

  const [rawHtml, setRawHtml] = useState('');
  const [analyzing, setAnalyzing] = useState(false);

  const [extraction, setExtraction] = useState(null);
  const [templatePreview, setTemplatePreview] = useState('');

  const [componentSelection, setComponentSelection] = useState({ elements: [], active: false });
  const [components, setComponents] = useState([]);
  const [editingComponent, setEditingComponent] = useState(null);

  const [hoverBox, setHoverBox] = useState(null);
  const [selectedBox, setSelectedBox] = useState(null);

  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    title: '',
    tid: '',
    description: '',
  });

  // Step 1: Fetch page
  const fetchPage = async () => {
    setFetching(true);
    setError('');
    try {
      const res = await window.axios.post('/api/v1/webpages/import-wizard/fetch', { url: sourceUrl });
      const html = (res.data && res.data.html) || '';
      setRawHtml(html);
      setStep(2);

      // Auto-advance to analysis
      window.setTimeout(() => analyzeWithAI(html), 100);
    } catch (e) {
      setError((e.response && e.response.data && e.response.data.error) || 'Failed to fetch page');
    } finally {
      setFetching(false);
    }
  };

  // Step 2: AI analysis
  const analyzeWithAI = async (html) => {
    setAnalyzing(true);
    setError('');
    setAnalysisStatus('Preparing…');

    try {
      const htmlForPrompt = (() => {
        const max = 50000;
        if (!html || html.length <= max) return html || '';

        // Include both the start and end of the document.
        // Many sites put the footer and critical CSS/JS near the end.
        const headSize = 30000;
        const tailSize = max - headSize;
        return [
          html.slice(0, headSize),
          '\n\n<!-- SNIP: middle removed to fit context -->\n\n',
          html.slice(-tailSize),
        ].join('');
      })();

      // Generate unique ID for this import to namespace CSS
      const importUid = 'imp_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 5);

      // Load the conversion guide
      const guideRes = await window.axios.get('/documents/convert_website_to_dashboard_templates.md');
      const guideText = guideRes.data || '';

      const prompt = [
        'You are converting a legacy website into Dashboard Online templates.',
        'Use the guide below to structure your response.',
        '',
        '=== CONVERSION GUIDE ===',
        guideText,
        '=== END GUIDE ===',
        '',
        'Task: Analyze the provided HTML and extract:',
        '1. header_html: The site header/navigation (semantic <header> tag or top navigation)',
        '2. main_html: The main body content (semantic <main> or primary content area)',
        '3. footer_html: The site footer (semantic <footer> or bottom section)',
        '4. site_css: All CSS needed for the template (inline styles + link tags)',
        '',
        'CRITICAL CSS NAMESPACING:',
        `- Postfix ALL CSS class names with "-${importUid}" to avoid conflicts`,
        `- Postfix ALL CSS ID selectors with "-${importUid}" to avoid conflicts`,
        '- Update both the CSS rules AND the HTML class/id attributes',
        `- Example: class="header" becomes class="header-${importUid}"`,
        `- Example: #main becomes #main-${importUid}`,
        `- Example CSS: .nav {} becomes .nav-${importUid} {}`,
        '- Apply to ALL classes and IDs without exception',
        '- Do NOT modify Bootstrap classes (btn, container, row, col-*, etc.)',
        '- Do NOT modify data attributes or other non-class/id attributes',
        '',
        'Rules from guide:',
        '- Extract fragments only (no <html>, <head>, or <body> tags)',
        '- The template will combine header + {{content}} + footer',
        '- Prefer semantic HTML tags when present',
        '- Include Bootstrap classes where appropriate',
        '- Extract complete CSS including fonts and icons',
        '',
        'Return STRICT JSON:',
        '{',
        '  "header_html": "...",',
        '  "main_html": "...",',
        '  "footer_html": "...",',
        '  "site_css": "...",',
        `  "import_uid": "${importUid}"`,
        '}',
        '',
        '=== HTML TO ANALYZE ===',
        htmlForPrompt, // Limit for token safety
      ].join('\n');

      // Prefer streaming status events (NDJSON) for better UX; fallback to /ai/query.
      const streamAiQuery = async () => {
        const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
        const abortController = new AbortController();
        const abortTimeout = window.setTimeout(() => {
          abortController.abort();
        }, 180000); // 3 minutes (fallback will take over)

        const resp = await fetch('/api/v1/ai/query/stream', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(csrf ? { 'X-CSRF-TOKEN': csrf } : {}),
          },
          credentials: 'same-origin',
          signal: abortController.signal,
          body: JSON.stringify({ prompt }),
        });

        window.clearTimeout(abortTimeout);

        if (!resp.ok || !resp.body) {
          throw new Error('Streaming not available');
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        let finalResponse = null;
        let sawDone = false;

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            let evt;
            try {
              evt = JSON.parse(trimmed);
            } catch {
              continue;
            }

            if (evt.type === 'status' && evt.message) {
              setAnalysisStatus(evt.message);
            }
            if (evt.type === 'error') {
              throw new Error(evt.message || 'AI streaming error');
            }
            if (evt.type === 'final') {
              finalResponse = evt.response || '';
              // Some servers/proxies keep the stream open; we can stop reading once we have the final payload.
              try {
                await reader.cancel();
              } catch {
                // ignore
              }
              sawDone = true;
              break;
            }
            if (evt.type === 'done') {
              sawDone = true;
              break;
            }
          }

          if (sawDone) {
            break;
          }
        }

        // Parse any trailing buffered line (in case the server didn't end with a newline)
        if (!sawDone && buffer.trim()) {
          try {
            const evt = JSON.parse(buffer.trim());
            if (evt.type === 'final') {
              finalResponse = evt.response || '';
            }
          } catch {
            // ignore
          }
        }

        if (finalResponse === null) {
          throw new Error('No final AI response received');
        }

        return finalResponse;
      };

      let raw;
      try {
        raw = await streamAiQuery();
      } catch {
        setAnalysisStatus('Streaming unavailable; waiting for AI…');
        const res = await window.axios.post('/ai/query', { prompt });
        raw = (res.data && (res.data.response || res.data)) || '';
      }

      const parsed = safeJsonParse(raw);

      const hasString = (v) => typeof v === 'string';

      // Header/footer can legitimately be empty strings on some pages.
      // Main content must be present.
      if (!parsed || !hasString(parsed.main_html) || !parsed.main_html.trim()) {
        setError('AI could not extract page structure. Please try a different page.');
        return;
      }

      if (!hasString(parsed.header_html)) parsed.header_html = '';
      if (!hasString(parsed.footer_html)) parsed.footer_html = '';

      // Store the import UID for reference
      if (parsed.import_uid) {
        parsed._import_uid = parsed.import_uid;
      }

      setExtraction(parsed);

      // Build template preview
      const preview = [
        parsed.header_html || '',
        '\n<!-- {{content}} will be injected here -->\n',
        '<div class="content-placeholder" style="padding: 40px; background: #f0f0f0; border: 2px dashed #999;">',
        '<p style="margin: 0; text-align: center; color: #666;">Page content will appear here ({{content}})</p>',
        '</div>',
        '\n',
        parsed.footer_html || '',
      ].join('');

      setTemplatePreview(preview);

      // Show import UID info
      if (parsed._import_uid) {
        console.log('Import UID for CSS namespacing:', parsed._import_uid);
      }

      setStep(3);
    } catch (e) {
      setError('AI analysis failed. ' + (e.message || ''));
    } finally {
      setAnalysisStatus('');
      setAnalyzing(false);
    }
  };

  // Step 3: Component selection mode
  const enableComponentSelection = () => {
    setComponentSelection({ elements: [], active: true });
  };

  const saveComponent = async () => {
    if (!editingComponent) return;

    const { title, description } = editingComponent;
    const tid = editingComponent.tid || toSnakeCase(title);

    if (!title || !tid) {
      setError('Component title and TID are required');
      return;
    }

    const html = componentSelection.elements.map((el) => el.html).join('\n');

    try {
      const res = await window.axios.post('/api/v1/webpages/components', {
        title,
        tid,
        description,
        html,
        type: 'component',
      });

      const newComp = { title, tid, description, html, token: `{{COMP:${tid}}}` };
      setComponents((prev) => [...prev, newComp]);
      setComponentSelection({ elements: [], active: false });
      setEditingComponent(null);
      setSelectedBox(null);
    } catch (e) {
      setError('Failed to save component: ' + ((e.response && e.response.data && e.response.data.error) || e.message));
    }
  };

  // Step 4: Save template
  const saveTemplate = async () => {
    setSavingTemplate(true);
    setError('');

    try {
      if (!extraction) {
        setError('No extracted content available');
        return;
      }

      const { title, tid, description } = templateForm;
      const finalTid = tid || toSnakeCase(title);

      if (!title || !finalTid) {
        setError('Template title and TID are required');
        return;
      }

      // Build the base template HTML
      let templateHtml = [
        extraction.header_html || '',
        '\n{{content}}\n',
        extraction.footer_html || '',
      ].join('');

      // Replace saved components with tokens
      for (const comp of components) {
        // Simple replacement (in production, use more robust matching)
        templateHtml = templateHtml.replace(comp.html, comp.token);
      }

      const res = await window.axios.post('/api/v1/webpages/templates', {
        title,
        tid: finalTid,
        description,
        html: templateHtml,
        css: extraction.site_css || '',
        type: 'template',
      });

      alert(`Template "${title}" saved successfully!`);
      window.location.href = '/webpages';
    } catch (e) {
      setError('Failed to save template: ' + ((e.response && e.response.data && e.response.data.error) || e.message));
    } finally {
      setSavingTemplate(false);
    }
  };

  // Auto-generate TID from title
  useEffect(() => {
    if (templateForm.title && !templateForm.tidManuallyEdited) {
      setTemplateForm((prev) => ({ ...prev, tid: toSnakeCase(prev.title) }));
    }
  }, [templateForm.title, templateForm.tidManuallyEdited]);

  // Iframe interaction handlers
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || step !== 3) return;

    let cleanup = null;

    const onLoad = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;

        const handleMove = (e) => {
          if (!componentSelection.active) return;
          const el = doc.elementFromPoint(e.clientX, e.clientY);
          if (!el || el.tagName.toLowerCase() === 'html' || el.tagName.toLowerCase() === 'body') {
            setHoverBox(null);
            return;
          }
          const rect = el.getBoundingClientRect();
          setHoverBox({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });
        };

        const handleClick = (e) => {
          if (!componentSelection.active) return;
          e.preventDefault();
          e.stopPropagation();

          const el = doc.elementFromPoint(e.clientX, e.clientY);
          if (!el || el.tagName.toLowerCase() === 'html' || el.tagName.toLowerCase() === 'body') return;

          const rect = el.getBoundingClientRect();
          setSelectedBox({ top: rect.top, left: rect.left, width: rect.width, height: rect.height });

          const html = el.outerHTML;
          setComponentSelection((prev) => ({
            ...prev,
            elements: [...prev.elements, { html, tag: el.tagName }],
          }));
        };

        doc.addEventListener('mousemove', handleMove);
        doc.addEventListener('click', handleClick, true);

        cleanup = () => {
          doc.removeEventListener('mousemove', handleMove);
          doc.removeEventListener('click', handleClick, true);
        };
      } catch (e) {
        // ignore
      }
    };

    iframe.addEventListener('load', onLoad);

    return () => {
      iframe.removeEventListener('load', onLoad);
      if (cleanup) cleanup();
    };
  }, [componentSelection.active, step]);

  const previewSrcDoc = useMemo(() => {
    if (!extraction) return '';

    const css = `<style>${extraction.site_css || ''}</style>`;
    const html = templatePreview || '';

    return `<!doctype html><html><head><meta charset="utf-8">${css}</head><body>${html}</body></html>`;
  }, [extraction, templatePreview]);

  return (
    <div>
      <div className="page-header" style={{ marginTop: 0 }}>
        <h3 style={{ marginTop: 0 }}>
          {pageTitle}
          <button
            className="btn btn-info btn-sm"
            onClick={() => setShowHelp(true)}
            style={{ marginLeft: 15 }}
          >
            <i className="fa fa-question-circle"></i> Help
          </button>
        </h3>
        <p className="text-muted">
          AI-assisted conversion: Fetch → Analyze → Extract Components → Save Template
        </p>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}

      {/* Help Modal */}
      {showHelp ? (
        <div className="modal fade in" style={{ display: 'block' }} onClick={() => setShowHelp(false)}>
          <div className="modal-dialog modal-lg" onClick={(e) => e.stopPropagation()}>
            <ImportWizardHelp />
          </div>
        </div>
      ) : null}

      {/* Step 1: Fetch */}
      {step === 1 ? (
        <div className="panel panel-default">
          <div className="panel-heading"><strong>Step 1: Fetch Website</strong></div>
          <div className="panel-body">
            <div className="form-group">
              <label>Website URL</label>
              <input
                className="form-control"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder="https://example.com"
              />
              <p className="help-block">Enter the full URL of the website you want to convert to a template.</p>
            </div>
            <button className="btn btn-primary" onClick={fetchPage} disabled={fetching || !sourceUrl}>
              {fetching ? 'Fetching…' : 'Fetch & Analyze'}
            </button>
          </div>
        </div>
      ) : null}

      {/* Step 2: Analyzing */}
      {step === 2 && analyzing ? (
        <div className="panel panel-info">
          <div className="panel-body">
            <div className="text-center">
              <i className="fa fa-spinner fa-spin fa-3x"></i>
              <p style={{ marginTop: 20 }}>Analyzing page structure with AI…</p>
              {analysisStatus ? (
                <p className="text-muted" style={{ marginTop: 10 }}>{analysisStatus}</p>
              ) : null}
              <p className="text-muted">Extracting header, main content, footer, and CSS</p>
            </div>
          </div>
        </div>
      ) : null}

      {/* Step 3: Preview & Component Extraction */}
      {step === 3 && extraction ? (
        <div>
          <div className="panel panel-success">
            <div className="panel-heading"><strong>Step 2: Template Extracted</strong></div>
            <div className="panel-body">
              <p>✓ Header, main content, and footer have been extracted.</p>
              <p>Preview below shows the template structure. Click "Select Components" to mark reusable sections.</p>
            </div>
          </div>

          <div className="row">
            <div className="col-md-8">
              <div className="panel panel-default">
                <div className="panel-heading">
                  <strong>Template Preview</strong>
                  {componentSelection.active ? (
                    <span className="label label-warning" style={{ marginLeft: 10 }}>Component Selection Mode: Click elements to select</span>
                  ) : null}
                </div>
                <div className="panel-body" style={{ padding: 0 }}>
                  <div style={{ position: 'relative', height: 600, overflow: 'hidden' }}>
                    <iframe
                      ref={iframeRef}
                      title="template-preview"
                      style={{ width: '100%', height: '100%', border: 0 }}
                      sandbox="allow-same-origin"
                      srcDoc={previewSrcDoc}
                    />
                    {hoverBox && componentSelection.active ? (
                      <div
                        style={{
                          position: 'absolute',
                          top: hoverBox.top,
                          left: hoverBox.left,
                          width: hoverBox.width,
                          height: hoverBox.height,
                          border: '2px solid #337ab7',
                          pointerEvents: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    ) : null}
                    {selectedBox && componentSelection.active ? (
                      <div
                        style={{
                          position: 'absolute',
                          top: selectedBox.top,
                          left: selectedBox.left,
                          width: selectedBox.width,
                          height: selectedBox.height,
                          border: '2px solid #5cb85c',
                          pointerEvents: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="panel panel-default">
                <div className="panel-heading"><strong>Actions</strong></div>
                <div className="panel-body">
                  {!componentSelection.active ? (
                    <div>
                      <button className="btn btn-info btn-block" onClick={enableComponentSelection}>
                        <i className="fa fa-hand-pointer-o"></i> Select Components
                      </button>
                      <p className="help-block">
                        Mark reusable sections (nav, hero, footer blocks, etc.) as components.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <div className="alert alert-info">
                        <strong>Selected:</strong> {componentSelection.elements.length} element(s)
                      </div>

                      {componentSelection.elements.length > 0 ? (
                        <div>
                          <div className="form-group">
                            <label>Component Title</label>
                            <input
                              className="form-control"
                              value={editingComponent?.title || ''}
                              onChange={(e) =>
                                setEditingComponent((prev) => ({ ...prev, title: e.target.value }))
                              }
                              placeholder="e.g. Main Navigation"
                            />
                          </div>
                          <div className="form-group">
                            <label>Component TID (auto-generated)</label>
                            <input
                              className="form-control"
                              value={editingComponent?.tid || toSnakeCase(editingComponent?.title || '')}
                              onChange={(e) =>
                                setEditingComponent((prev) => ({ ...prev, tid: e.target.value }))
                              }
                              placeholder="e.g. main_navigation"
                            />
                          </div>
                          <div className="form-group">
                            <label>Description (optional)</label>
                            <textarea
                              className="form-control"
                              rows={2}
                              value={editingComponent?.description || ''}
                              onChange={(e) =>
                                setEditingComponent((prev) => ({ ...prev, description: e.target.value }))
                              }
                            />
                          </div>
                          <button className="btn btn-success btn-block" onClick={saveComponent}>
                            Save Component
                          </button>
                        </div>
                      ) : null}

                      <button
                        className="btn btn-default btn-block"
                        onClick={() => setComponentSelection({ elements: [], active: false })}
                      >
                        Cancel Selection
                      </button>
                    </div>
                  )}

                  <hr />

                  <h5>Saved Components ({components.length})</h5>
                  {components.length > 0 ? (
                    <ul className="list-group" style={{ marginBottom: 10 }}>
                      {components.map((c, i) => (
                        <li key={i} className="list-group-item">
                          <strong>{c.title}</strong>
                          <br />
                          <code>{c.token}</code>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted">No components saved yet.</p>
                  )}

                  <hr />

                  <button className="btn btn-primary btn-block" onClick={() => setStep(4)}>
                    Next: Save Template →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Step 4: Save Template */}
      {step === 4 && extraction ? (
        <div className="panel panel-default">
          <div className="panel-heading"><strong>Step 3: Save Template</strong></div>
          <div className="panel-body">
            <div className="form-group">
              <label>Template Title</label>
              <input
                className="form-control"
                value={templateForm.title}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, title: e.target.value }))}
                placeholder="e.g. My Site Template"
              />
            </div>
            <div className="form-group">
              <label>Template TID (auto-generated, editable)</label>
              <input
                className="form-control"
                value={templateForm.tid}
                onChange={(e) =>
                  setTemplateForm((prev) => ({ ...prev, tid: e.target.value, tidManuallyEdited: true }))
                }
                placeholder="e.g. my_site_template"
              />
              <p className="help-block">This will be used as the template's unique identifier.</p>
            </div>
            <div className="form-group">
              <label>Description (optional)</label>
              <textarea
                className="form-control"
                rows={3}
                value={templateForm.description}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="alert alert-success">
              <strong>Template Structure:</strong>
              <ul>
                <li>Header + Footer extracted</li>
                <li>Components: {components.length} saved</li>
                <li>CSS: {extraction.site_css ? 'Included' : 'None'}</li>
                {extraction._import_uid ? (
                  <li>Import ID: <code>{extraction._import_uid}</code> (CSS namespaced)</li>
                ) : null}
              </ul>
            </div>

            <button
              className="btn btn-success btn-lg"
              onClick={saveTemplate}
              disabled={savingTemplate || !templateForm.title}
            >
              {savingTemplate ? 'Saving…' : 'Save Template'}
            </button>
            <button
              className="btn btn-default"
              onClick={() => setStep(3)}
              style={{ marginLeft: 10 }}
            >
              ← Back
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
